import { useState } from 'react';
import { FEATURES as FEATURE_DATA } from '../data/features.js';

// Icon, name and href come from the one feature registry so they cannot drift
// from the nav or the feature pages. The homepage-only extras — the wide image
// variant, the pull quote and its attribution — stay here, keyed by slug.
//
// ATTRIBUTION RULE: every quote here must trace to a real review on
// https://apps.shopify.com/account-editor, cited as store · country · date.
// Never attribute a quote to a named individual, and never to a company that
// isn't a real reviewer — an earlier revision of this file carried five invented
// personas, one of them pinned on a real and unaffiliated brand, which is
// deceptive under FTC endorsement rules however plausible the wording reads.
// Store-level attribution is what the App Store itself publishes, and it is the
// only form we can stand behind.
const EXTRAS = {
  'self-serve-edits': {
    img: '/images/self-serve-edits-1.webp',
    desc: 'Sizes, colors, quantities, variants, addresses \u2014 all editable from the order confirmation. No tickets, no waiting, no manual ops.',
    quote:
      '"We used to field a ticket for every minor edit. Customers handle it themselves now, so our support staff aren\'t the bottleneck."',
    cite: '\u2014 One Stop Systems \u00b7 United States \u00b7 Apr 11, 2025',
  },
  'cancel-deflection': {
    img: '/images/cancel-deflection-1.webp',
    desc: 'Offer store credit, free upgrades, or smart alternatives the moment a customer hits "cancel" \u2014 recover up to 38% of would-be refunds.',
    quote:
      '"The smart cancellation settings make cancellations far easier to manage \u2014 we set our own reasons and rules."',
    cite: '\u2014 Hey Duvet\u2122 \u00b7 Australia \u00b7 Apr 15, 2025',
  },
  'post-purchase-upsells': {
    img: '/images/post-purchase-upsells-1.webp',
    desc: 'Land upsells after checkout but before the warehouse picks. AOV lifts 9\u201317% out of the box.',
    quote:
      '"Upsells added a 15% lift in AOV within a month, and support was fast whenever we needed them."',
    cite: '\u2014 SHAE & PALMER \u00b7 United States \u00b7 May 1, 2026',
  },
  'operations-analytics': {
    img: '/images/operations-analytics-1.webp',
    desc: 'Edit volume, deflection rate, recovered revenue, AOV lift \u2014 reconciled to your Shopify ledger.',
    quote: '"The app and the audit report have been great to have."',
    cite: '\u2014 Kyser\u00ae Musical Products \u00b7 United States \u00b7 Jul 10, 2025',
  },
  'address-validation': {
    img: '/images/address-validation-1.webp',
    desc: 'Google-validated address corrections on every order. Customers fix typos before the package gets stuck.',
    quote:
      '"We brought it on to cut shipping-address enquiries, and we\'ve had almost none since."',
    cite: '\u2014 Paola Pacheco Jewelry \u00b7 United States \u00b7 Aug 4, 2025',
  },
};

const FEATURES = FEATURE_DATA.map((f) => ({
  icon: f.icon,
  name: f.label,
  href: `/features/${f.slug}`,
  ...EXTRAS[f.slug],
}));

export default function Features() {
  const [open, setOpen] = useState(0);
  const visual = FEATURES[open]?.img || FEATURES[0].img;

  return (
    <section className="features">
      <div className="features__container">
        <div className="features__content">
          <div className="features__head">
            <span className="section-label">Everything in one app</span>
            <h2 className="section-title">Take Control of Your Operations Experience</h2>
            <p className="features__sub">
              Pick a workflow. Every edit, every save, every upsell — <strong>in one place.</strong>
            </p>
          </div>

          <div className="features__accordion">
            {FEATURES.map((f, i) => {
              const isOpen = i === open;
              return (
                <div className={`feature-item${isOpen ? ' is-open' : ''}`} key={f.name}>
                  <button
                    type="button"
                    className="feature-item__head"
                    aria-expanded={isOpen}
                    onClick={() => setOpen(isOpen ? open : i)}
                  >
                    <span className="feature-item__left">
                      <span className="feature-item__icon"><i className={`ph ${f.icon}`} aria-hidden="true" /></span>
                      <span className="feature-item__name">{f.name}</span>
                    </span>
                    <span className="feature-item__toggle">
                      <i className={`ph ${isOpen ? 'ph-minus' : 'ph-plus'}`} aria-hidden="true" />
                    </span>
                  </button>
                  <div className="feature-item__body">
                    <p className="feature-item__desc">{f.desc}</p>
                    <blockquote className="feature-item__quote">
                      {f.quote} <cite>{f.cite}</cite>
                    </blockquote>
                    <a href={f.href} className="btn btn--outline" aria-label={`Learn more about ${f.name}`}>
                      Learn more <i className="ph ph-arrow-right" aria-hidden="true" />
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="features__visual">
          <img
            src={visual}
            alt="Account Editor order editing interface"
            className="features__visual-img"
          />
        </div>
      </div>
    </section>
  );
}
